// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { format, parseISO } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { useState } from "react";
import type { Matcher } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDate } from "@/lib/format";
import { useDateFormat } from "@/lib/preferences";
import { cn } from "@/lib/utils";

interface DatePickerProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  fromYear?: number;
  toYear?: number;
  /** Last month navigation reaches, in full. Finer than `toYear`, and wins over it. */
  toMonth?: Date;
  /**
   * Greys out every day after this date. `toYear` only bounds month navigation, so a picker that
   * must refuse future days (a replay of a day that has already been delivered, say) needs this.
   */
  disableAfter?: Date;
  /** Greys out every day before this date. */
  disableBefore?: Date;
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  fromYear,
  toYear,
  toMonth,
  disableAfter,
  disableBefore,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [dateFormat] = useDateFormat();
  const selected = value ? parseISO(value) : undefined;
  const disabledMatchers: Matcher[] = [];
  if (disableAfter) disabledMatchers.push({ after: disableAfter });
  if (disableBefore) disabledMatchers.push({ before: disableBefore });

  function handleSelect(date: Date | undefined) {
    // `yyyy-MM-dd` here is the emitted *value*, not a display format — never localise it.
    onChange(date ? format(date, "yyyy-MM-dd") : undefined);
    setOpen(false);
  }

  return (
    <div className="relative w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              // `gap-2.5` overrides the button's own `gap-2` so the icon sits the same
              // 10px from its label as the search field's does — this control lines up
              // beside one in every filter bar it appears in, and 8px read as tighter.
              "w-full justify-start gap-2.5 text-left font-normal",
              !selected && "text-muted-foreground",
              selected && "pr-8",
            )}
          >
            {/* No `mr-2` here: the button is already a flex row with a gap, so a margin
                on top of it doubled the spacing to 16px. */}
            <CalendarIcon className="h-4 w-4 opacity-50" />
            {selected ? formatDate(selected, dateFormat) : <span>{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar
            mode="single"
            captionLayout="dropdown"
            selected={selected}
            // Open on the chosen date's month, not today's: the popover remounts on every open
            defaultMonth={selected}
            onSelect={handleSelect}
            startMonth={fromYear ? new Date(fromYear, 0) : undefined}
            endMonth={toMonth ?? (toYear ? new Date(toYear, 11) : undefined)}
            // Separate matchers, not one `{after, before}` object — that shape is react-day-picker's
            // DateInterval and would disable the days *between* the two bounds instead.
            disabled={disabledMatchers.length ? disabledMatchers : undefined}
          />
        </PopoverContent>
      </Popover>
      {selected && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Clear date"
          className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 opacity-50 hover:opacity-100"
          onClick={() => onChange(undefined)}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}
